"use client";

import Link from "next/link";
import Script from "next/script";
import { useState, useRef } from "react";
import NavBar from "@/components/marketing/navbar";
import Footer from "@/components/marketing/footer";

const WEBHOOK_URL = "https://auto.davidsoden.com/webhook/leads";
const CALCOM_URL = "https://cal.com/dsoden/30min";

const BLOCKED_DOMAINS = [
  "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "ymail.com",
  "hotmail.com", "hotmail.co.uk", "outlook.com", "live.com", "msn.com",
  "aol.com", "icloud.com", "me.com", "mac.com",
  "protonmail.com", "proton.me", "tutanota.com", "zoho.com",
  "mail.com", "email.com", "gmx.com", "gmx.net",
  "yandex.com", "yandex.ru", "inbox.com",
  "fastmail.com", "hushmail.com", "mailfence.com",
  "guerrillamail.com", "tempmail.com", "throwaway.email",
  "mailinator.com", "sharklasers.com", "guerrillamailblock.com",
  "grr.la", "dispostable.com", "10minutemail.com", "trashmail.com",
];

function isBusinessEmail(email: string) {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return !BLOCKED_DOMAINS.includes(domain);
}

export default function DemoPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");
  const [calUrl, setCalUrl] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const handleEmailBlur = () => {
    if (email && !isBusinessEmail(email)) {
      setEmailError("Please use your business email address — no personal email providers.");
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isBusinessEmail(email)) {
      setEmailError("Please use your business email address — no personal email providers.");
      return;
    }

    const turnstileToken = (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value;
    if (!turnstileToken) {
      setFormStatus("error");
      setFormMessage("Please complete the security verification.");
      return;
    }

    setFormStatus("submitting");
    setFormMessage("");

    try {
      const prefixedMessage = `***CONTRACT LUCIDITY LEAD***\n\n${message}`;

      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          company,
          industry: "legal",
          message: prefixedMessage,
          "cf-turnstile-response": turnstileToken,
        }),
      });

      const result = await response.json();

      if (response.ok && result.response === "success") {
        const notes = `Company: ${company} | Industry: Legal\n\n${message}`;
        const url =
          CALCOM_URL +
          "?name=" + encodeURIComponent(firstName + " " + lastName) +
          "&email=" + encodeURIComponent(email) +
          "&notes=" + encodeURIComponent(notes);

        setCalUrl(url);
        setFormStatus("success");
        setFormMessage("Your information has been received — click the button below to schedule your live demo.");
      } else {
        throw new Error(result.message || "Unexpected response from server.");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("Something went wrong. Please try again or email david@davidsoden.com directly.");
    }
  };

  const handleScheduleClick = () => {
    window.open(calUrl, "_blank");
    // Reset form
    setFirstName("");
    setLastName("");
    setEmail("");
    setCompany("");
    setMessage("");
    setFormStatus("idle");
    setFormMessage("");
    setCalUrl("");
    if ((window as any).turnstile) (window as any).turnstile.reset();
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
      <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />

      <NavBar />

      {/* ─── HERO ─── */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-6" style={{ fontFamily: "system-ui, sans-serif" }}>
            Request a Demo
          </p>
          <h1 className="text-4xl md:text-5xl leading-[1.15] mb-6 text-[#1B3A6B]">
            See every clause reviewed.<br />
            <span className="text-[#C8A84B]">In under 5 minutes.</span>
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
            Upload a real contract and watch Contract Lucidity analyze it clause by clause, flag deviations, and generate a redlined Word document — with your attorney in control at every step.
          </p>
        </div>
      </section>

      {/* ─── FORM ─── */}
      <section className="pb-24">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-[#F8F9FB] rounded-xl p-8 md:p-10 border border-gray-100 shadow-sm">
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#1B3A6B] mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                    style={{ fontFamily: "system-ui, sans-serif" }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1B3A6B] mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                    style={{ fontFamily: "system-ui, sans-serif" }}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1B3A6B] mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>Work Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  onBlur={handleEmailBlur}
                  placeholder="you@firm.com"
                  required
                  className={`w-full px-4 py-3 rounded-lg border bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B] ${emailError ? "border-red-400" : "border-gray-200"}`}
                  style={{ fontFamily: "system-ui, sans-serif" }}
                />
                {emailError && <p className="text-red-500 text-xs mt-1" style={{ fontFamily: "system-ui, sans-serif" }}>{emailError}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1B3A6B] mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>Firm / Company</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B]"
                  style={{ fontFamily: "system-ui, sans-serif" }}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#1B3A6B] mb-1.5" style={{ fontFamily: "system-ui, sans-serif" }}>
                  What types of contracts does your team review? Any specific pain points?
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., We review 50+ vendor MSAs per month and spend too much time on clause-by-clause comparison..."
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B] resize-none"
                  style={{ fontFamily: "system-ui, sans-serif" }}
                />
              </div>

              <div className="cf-turnstile mb-4" data-sitekey="0x4AAAAAACfM9OUbiTZyvEUC" data-theme="light" />

              {formMessage && (
                <div
                  className={`text-sm rounded-lg px-4 py-3 mb-4 ${
                    formStatus === "success" ? "bg-green-50 text-green-700 border border-green-200" :
                    formStatus === "error" ? "bg-red-50 text-red-700 border border-red-200" : ""
                  }`}
                  style={{ fontFamily: "system-ui, sans-serif" }}
                >
                  {formMessage}
                </div>
              )}

              {formStatus === "success" ? (
                <button
                  type="button"
                  onClick={handleScheduleClick}
                  className="w-full py-4 text-sm font-semibold bg-[#22C55E] text-white rounded-lg hover:bg-[#1ea34d] transition-colors flex items-center justify-center gap-2"
                  style={{ fontFamily: "system-ui, sans-serif" }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Your Demo
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded-lg hover:bg-[#2E5EA0] transition-colors disabled:opacity-60"
                  style={{ fontFamily: "system-ui, sans-serif" }}
                >
                  {formStatus === "submitting" ? "Submitting..." : "Request a Live Demo"}
                </button>
              )}
            </form>

            <p className="text-xs text-gray-400 text-center mt-4" style={{ fontFamily: "system-ui, sans-serif" }}>
              Your information is confidential. We&apos;ll respond within 24 hours to schedule your personalized demonstration.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
